import * as api from '@api';
import ErrorToast from '@components/ErrorToast';
import { ROWS_PER_PAGE_SELECTOR, USER_PREFERENCE_KEY_ENUMS } from '@constants';
import { getColumnFilter } from '@lib/util';
import { useAuth } from '@state/auth';
import { debounce } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useToast from '@/hooks/useToast';

export default function useTable({
  paginationMetaData,
  data,
  onSelectedRowsChanged,
  onAllRowsSelectedChanged,
  tablePreferencesKey,
  savePreferences = true,
  initialRowsPerPage = ROWS_PER_PAGE_SELECTOR[2],
  disableUrlSync = false,
}) {
  const { toast } = useToast();
  const { me, meReceived } = useAuth(state => ({
    me: state.me,
    meReceived: state.meReceived,
  }));
  const [pagination, setPagination] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAllRowsSelected, setIsAllRowsSelected] = useState(false);
  const [savedColumns, setSavedColumns] = useState([]);
  const [count, setCount] = useState(
    String(
      disableUrlSync
        ? initialRowsPerPage
        : searchParams.get('count') || initialRowsPerPage,
    ),
  );
  const [filterStateRetrieved, setFilterStateRetrieved] = useState(
    !!disableUrlSync,
  );
  const [filterValue, setFilterValue] = useState(() => {
    if (disableUrlSync) return {};
    try {
      return JSON.parse(searchParams.get('filter')) || {};
    } catch {
      return {};
    }
  });
  const [localPage, setLocalPage] = useState('0');
  const [localSort, setLocalSort] = useState(null);
  const userSetFilter = searchParams.get('filter');
  const location = useLocation();
  const navigate = useNavigate();

  // Sync filterValue state with URL searchParams
  useEffect(() => {
    if (disableUrlSync) return;
    const urlFilter = (() => {
      try {
        return JSON.parse(searchParams.get('filter')) || {};
      } catch {
        return {};
      }
    })();
    setFilterValue(urlFilter);
  }, [searchParams, disableUrlSync]);

  const getSavedTablePreference = key => {
    let savedPreference =
      me?.preferences?.[key] && Object.keys(me.preferences[key]).length > 0
        ? JSON.stringify(me.preferences[key])
        : null;
    savedPreference =
      savedPreference == null ? [] : JSON.parse(savedPreference);
    savedPreference = savedPreference.filter(
      c => c.table === tablePreferencesKey,
    );
    return savedPreference;
  };

  // Raw updater (un-debounced)
  const updatePreferencesInDb = ({ preferences, isResize }) => {
    api.backend
      .mePreferencesPatch([
        {
          key: USER_PREFERENCE_KEY_ENUMS.SAVED_TABLE_PREFERENCES,
          value: preferences[
            USER_PREFERENCE_KEY_ENUMS.SAVED_TABLE_PREFERENCES
          ].map(p => {
            if (!p.rowsPerPage) {
              return { ...p, rowsPerPage: 50 };
            }
            return p;
          }),
        },
      ])
      .then(response => {
        if (response.status >= 400) {
          toast.custom(t => <ErrorToast t={t} error={response.error} />);
        } else {
          if (!isResize) {
            toast.success('Preferences Saved successfully', { duration: 5000 });
          }
          meReceived({
            ...me,
            preferences: response.data,
            mePreferencesUpdated: false,
          });
        }
      });
  };

  // Debounced wrapper (500ms default). Adjust delay as needed.
  const debouncedUpdateRef = useRef(
    debounce(updatePreferencesInDb, 500, { leading: false, trailing: true }),
  );

  // Flush debounced calls on unmount to avoid losing last change
  useEffect(() => {
    return () => {
      debouncedUpdateRef.current.flush();
    };
  }, []);

  const saveTablePreferences = (type, value, isResize) => {
    if (!tablePreferencesKey) {
      toast.error('Table preferences key missing');
      return;
    }

    if (!['filter', 'columnReordering', 'rowsPerPage'].includes(type)) {
      throw 'type should either be filter | columnReordering | rowsPerPage';
    }
    const tablesPreferencesKey =
      USER_PREFERENCE_KEY_ENUMS.SAVED_TABLE_PREFERENCES;
    const tablePreferences =
      JSON.parse(
        me?.preferences?.[tablesPreferencesKey] &&
          Object.keys(me.preferences[tablesPreferencesKey]).length > 0
          ? JSON.stringify(me.preferences[tablesPreferencesKey])
          : null,
      ) || [];
    const tablePreferenceIndex = tablePreferences.findIndex(
      t => t.table === tablePreferencesKey,
    );
    if (tablePreferenceIndex > -1) {
      tablePreferences[tablePreferenceIndex] = {
        ...tablePreferences[tablePreferenceIndex],
        [type]: value,
      };
    } else {
      const preference = {
        table: tablePreferencesKey,
        [type]: value,
      };
      tablePreferences.push(preference);
    }

    debouncedUpdateRef.current({
      preferences: {
        ...me?.preferences,
        [tablesPreferencesKey]: tablePreferences,
      },
      isResize,
    });

    if (!['filter', 'rowsPerPage'].includes(type) && !isResize) {
      setSavedColumns(value);
    }
  };

  useEffect(() => {
    if (disableUrlSync) return;
    setCount(String(searchParams.get('count') || ROWS_PER_PAGE_SELECTOR[2]));
  }, [searchParams.get('count'), disableUrlSync]);

  useEffect(() => {
    if (!savePreferences || disableUrlSync) return;
    const columns = getSavedTablePreference(
      USER_PREFERENCE_KEY_ENUMS.SAVED_TABLE_PREFERENCES,
    );
    setSavedColumns(
      columns?.length > 0 ? columns[0].columnReordering || [] : [],
    );

    searchParams.set(
      'count',
      columns?.length > 0 ? columns[0].rowsPerPage : ROWS_PER_PAGE_SELECTOR[2],
    );
    let savedFilterData = getSavedTablePreference(
      USER_PREFERENCE_KEY_ENUMS.SAVED_TABLE_PREFERENCES,
    );
    savedFilterData =
      savedFilterData?.length > 0 ? savedFilterData[0].filter || [] : [];

    let newFilterValue = {};
    if (savedFilterData) {
      for (const [key, value] of Object.entries(savedFilterData)) {
        if (value?.length) {
          newFilterValue = { ...newFilterValue, [key]: value };
        }
        if (
          typeof value === 'object' &&
          !Array.isArray(value) &&
          value !== null
        ) {
          newFilterValue[key] = value.key || '';
        }
      }
      searchParams.set('filter', JSON.stringify(newFilterValue));
    }

    //if user has set filter, then use merge savedFilter with userSetFilter
    if (userSetFilter) {
      let userFilter;
      try {
        userFilter = JSON.parse(userSetFilter);
      } catch {
        userFilter = {};
      }
      for (const [key, value] of Object.entries(userFilter)) {
        if (value === '') delete userFilter[key];
      }
      if (Object.keys(userFilter).length > 0) {
        newFilterValue = { ...userFilter, ...newFilterValue };
        searchParams.set('filter', JSON.stringify(newFilterValue));
      }
    }

    setFilterValue(newFilterValue);
    setFilterStateRetrieved(true);

    const url = `${location.pathname}?${searchParams.toString()}`;
    navigate(url, { replace: true });
  }, []);

  const getSort = column => {
    const sort = disableUrlSync ? localSort : searchParams.get('sort');
    if (sort) {
      return sort.split(':')[0] === column ? sort.split(':')[1] : null;
    }
    return null;
  };

  const getSortOrder = column => {
    const sort = searchParams.get('sort');
    const order = searchParams.get('order');

    if (sort && order) {
      if (sort === column) {
        return order;
      }
    }
    return null;
  };

  const handleSort = order => {
    if (disableUrlSync) {
      setLocalPage('0');
      setLocalSort(order);
      return;
    }
    // New sort field are DESC. Existing ones are flipped
    searchParams.set('page', 0);
    searchParams.set('sort', order);
    setSearchParams(searchParams);
  };
  const handleSortOrder = (sort, order) => {
    searchParams.set('page', 0);
    searchParams.set('sort', sort);
    searchParams.set('order', order);
    setSearchParams(searchParams);
  };
  const handleColumnFilter = filterValue => {
    const userFilter = getColumnFilter(filterValue);
    if (disableUrlSync) {
      setFilterValue(Object.keys(userFilter).length > 0 ? userFilter : {});
      setLocalPage('0');
      return;
    }
    const newSearchParams = new URLSearchParams(searchParams);

    if (Object.keys(userFilter).length > 0) {
      newSearchParams.set('filter', JSON.stringify(userFilter));
      newSearchParams.set('page', 0);
    } else {
      newSearchParams.delete('filter');
    }
    setSearchParams(newSearchParams);
  };
  const handlePageChange = pageNumber => {
    const selectedPage = pageNumber - 1;
    if (disableUrlSync) {
      setLocalPage(String(selectedPage));
      return;
    }
    searchParams.set('page', selectedPage);
    setSearchParams(searchParams);
  };
  const handleRowsPerPageChanged = selectedRowsPerPage => {
    if (disableUrlSync) {
      setCount(String(selectedRowsPerPage));
      setLocalPage('0');
      return;
    }
    searchParams.set('count', selectedRowsPerPage);
    searchParams.set('page', 0);
    setSearchParams(searchParams);
    saveTablePreferences('rowsPerPage', selectedRowsPerPage, true);
  };

  useEffect(() => {
    if (!paginationMetaData) return;

    const newPagination = {
      pageNumbers:
        Math.ceil(
          paginationMetaData?.totalCount / paginationMetaData?.pageSize,
        ) || 1,
      rowsPerPage: paginationMetaData?.pageSize,
      currentPage: paginationMetaData?.page + 1 || 0,
      totalCount: paginationMetaData?.totalCount,
      rowsPerPageOptions: ROWS_PER_PAGE_SELECTOR,
    };

    // Only update if pagination has actually changed
    setPagination(prevPagination => {
      if (
        prevPagination.pageNumbers === newPagination.pageNumbers &&
        prevPagination.rowsPerPage === newPagination.rowsPerPage &&
        prevPagination.currentPage === newPagination.currentPage &&
        prevPagination.totalCount === newPagination.totalCount
      ) {
        return prevPagination; // No change, prevent re-render
      }
      return newPagination;
    });
  }, [
    paginationMetaData?.totalCount,
    paginationMetaData?.pageSize,
    paginationMetaData?.page,
  ]);

  useEffect(() => {
    setIsAllRowsSelected(data.filter(d => d.isSelected).length === data.length);
  }, [data]);

  const handleRowSelect = index => {
    const nextDataSource = [...data];
    nextDataSource[index] = {
      ...nextDataSource[index],
      isSelected: !nextDataSource[index].isSelected,
    };
    onSelectedRowsChanged(index, nextDataSource);
  };
  const handleAllRowsSelect = () => {
    const isAllSelected =
      data.filter(d => d.isSelected === true).length === data.length;

    const nextDataSource = data.map(dt => ({
      ...dt,
      isSelected: !isAllSelected,
    }));
    onAllRowsSelectedChanged(nextDataSource);
  };

  const toggleSelectAllRows = (select = true) => {
    const updatedDataSource = data.map(row => ({
      ...row,
      isSelected: select,
    }));
    onAllRowsSelectedChanged(updatedDataSource);
  };

  const handleSaveFilter = filterValue => {
    const userFilter = { ...filterValue };
    for (const [key, value] of Object.entries(filterValue)) {
      if (Array.isArray(value)) {
        userFilter[key] = '';
        for (const item of value) {
          userFilter[key] += userFilter[key] === '' ? item.key : `,${item.key}`;
        }
      }
    }

    if (saveTablePreferences('filter', userFilter, false) === true) {
      for (const [key, value] of Object.entries(userFilter)) {
        if (value === '') delete userFilter[key];
      }
      if (Object.keys(userFilter).length > 0) {
        searchParams.set('filter', JSON.stringify(userFilter));
      } else {
        searchParams.delete('filter');
      }
      setSearchParams(searchParams);
    }
  };

  return {
    filterStateRetrieved,
    filterValue,
    pagination,
    page: disableUrlSync ? localPage : searchParams.get('page') || '0',
    sort: disableUrlSync ? localSort : searchParams.get('sort'),
    order: disableUrlSync ? null : searchParams.get('order'),
    count,
    getSort,
    getSortOrder,
    handleSort,
    handleSortOrder,
    handlePageChange,
    handleRowsPerPageChanged,
    handleColumnFilter,
    handleRowSelect,
    handleAllRowsSelect,
    isAllRowsSelected,
    toggleSelectAllRows,
    saveTablePreferences,
    getSavedTablePreference,
    savedColumns,
    setSavedColumns,
    handleSaveFilter,
    searchParams,
  };
}
